import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Mail, Phone, MapPin, MoreVertical, User, Building2, Trash2, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { toast } from 'sonner'
import { clientsApi } from '@/lib/api'
import { ClientResponse } from '@/types'
import { Button } from '@/components/ui/button'
import { usePermissions } from '@/contexts/PermissionContext'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { formatDate } from '@/lib/utils'
import { Client } from '@/types'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]
const ALL_STATUSES = '__all__'
const ALL_COUNTRIES = '__all__'

interface ClientFilters {
  searchTerm: string
  country: string
  status: string
}

export default function ClientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { canAddEdit } = usePermissions()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)

  // Filter state
  const [filters, setFilters] = useState<ClientFilters>({
    searchTerm: '',
    country: ALL_COUNTRIES,
    status: ALL_STATUSES,
  })
  const [appliedFilters, setAppliedFilters] = useState<ClientFilters>({
    searchTerm: '',
    country: ALL_COUNTRIES,
    status: ALL_STATUSES,
  })

  // Fetch clients with server-side pagination
  const { data: clientsResponse, isLoading, error } = useQuery({
    queryKey: ['clients', currentPage, pageSize, appliedFilters],
    queryFn: () => clientsApi.getAll({
      page: currentPage,
      page_size: pageSize,
      search: appliedFilters.searchTerm || undefined,
      country: appliedFilters.country !== ALL_COUNTRIES ? appliedFilters.country : undefined,
      status: appliedFilters.status !== ALL_STATUSES ? appliedFilters.status : undefined,
    }),
  })

  const clients = clientsResponse?.clients || []
  const totalRecords = clientsResponse?.total || 0
  const totalPages = clientsResponse?.total_pages || 1

  // Get unique countries from clients for the filter dropdown
  // Note: This now only shows countries from the current page
  // For a complete list, we'd need a separate API endpoint
  const uniqueCountries = useMemo<string[]>(() => {
    const countries = new Set(clients.map((c: ClientResponse) => c.country).filter((c): c is string => !!c && c.trim() !== ''))
    return Array.from(countries).sort()
  }, [clients])

  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const endRecord = Math.min(currentPage * pageSize, totalRecords)

  const handlePageSizeChange = (newSize: string) => {
    setPageSize(Number(newSize))
    setCurrentPage(1) // Reset to first page when changing page size
  }

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  // Filter handlers
  const handleSearch = () => {
    setAppliedFilters(filters)
    setCurrentPage(1)
  }

  const clearFilters = () => {
    const clearedFilters = {
      searchTerm: '',
      country: ALL_COUNTRIES,
      status: ALL_STATUSES,
    }
    setFilters(clearedFilters)
    setAppliedFilters(clearedFilters)
    setCurrentPage(1)
  }

  // Handle Enter key press in search input
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  // Check if any filters are applied
  const hasActiveFilters = appliedFilters.searchTerm ||
    appliedFilters.country !== ALL_COUNTRIES ||
    appliedFilters.status !== ALL_STATUSES

  const deleteMutation = useMutation({
    mutationFn: (clientId: string) => clientsApi.delete(clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients'] })
      toast.success('Client marked as inactive successfully')
      setDeleteDialogOpen(false)
      setClientToDelete(null)
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Failed to mark client as inactive'
      if (error.response?.data?.invoice_count) {
        toast.error(`${errorMessage}. This client has ${error.response.data.invoice_count} invoice(s).`)
      } else {
        toast.error(errorMessage)
      }
    },
  })

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client)
    setDeleteDialogOpen(true)
  }

  const confirmDelete = () => {
    if (clientToDelete) {
      deleteMutation.mutate(clientToDelete.client_id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">{t('common.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-destructive">{t('common.failedToLoad')}</div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between -mt-2">
        <div>
          <h1 className="text-2xl font-bold">{t('clients.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('clients.getStarted')}
          </p>
        </div>
        {canAddEdit('CLIENTS') && (
          <Link to="/clients/new">
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              {t('clients.addClient')}
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center -mb-1">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-[300px]">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10 pointer-events-none bg-white dark:bg-neutral-800 rounded-sm">
            <Search className="h-4 w-4 text-muted-foreground" />
          </div>
          <Input
            placeholder={t('clients.searchPlaceholder')}
            value={filters.searchTerm}
            onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
            onKeyPress={handleKeyPress}
            className="pl-9"
          />
        </div>

        {/* Country */}
        <Select
          value={filters.country}
          onValueChange={(value) => {
            setFilters({ ...filters, country: value })
            setAppliedFilters({ ...appliedFilters, country: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder={t('clients.allCountries')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_COUNTRIES}>{t('clients.allCountries')}</SelectItem>
            {uniqueCountries.map((country) => (
              <SelectItem key={country} value={country}>
                {country}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status */}
        <Select
          value={filters.status}
          onValueChange={(value) => {
            setFilters({ ...filters, status: value })
            setAppliedFilters({ ...appliedFilters, status: value })
            setCurrentPage(1)
          }}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder={t('clients.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_STATUSES}>{t('clients.allStatuses')}</SelectItem>
            <SelectItem value="active">{t('common.active')}</SelectItem>
            <SelectItem value="inactive">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button onClick={handleSearch}>
          <Search className="mr-2 h-4 w-4" />
          {t('common.search')}
        </Button>

        {/* Clear Button */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <X className="mr-1 h-4 w-4" />
            {t('common.reset')}
          </Button>
        )}
      </div>

      {clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('clients.noClientsYet')} {t('clients.getStarted')}
            </div>
            {canAddEdit('CLIENTS') && (
              <Link to="/clients/new">
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('clients.addClient')}
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : clients.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="text-muted-foreground mb-4">
              {t('clients.noMatchingClients')}
            </div>
            <Button variant="outline" onClick={clearFilters}>
              <X className="mr-2 h-4 w-4" />
              {t('common.reset')} {t('common.filters')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col h-[calc(100vh-240px)]">
          <Card className="flex-1 overflow-hidden flex flex-col">
            <div className="flex-1 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10">
                  <TableRow>
                    <TableHead>{t('clients.clientName')}</TableHead>
                    <TableHead>{t('common.email')}</TableHead>
                    <TableHead>{t('common.phone')}</TableHead>
                    <TableHead>{t('common.country')}</TableHead>
                    <TableHead>{t('common.status')}</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="w-[100px]">{t('common.actions')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clients.map((client) => (
                    <TableRow key={client.client_id}>
                      <TableCell className="font-medium">{client.client_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {client.client_email}
                        </div>
                      </TableCell>
                      <TableCell>
                        {client.client_phone ? (
                          <div className="flex items-center gap-2">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            {client.client_phone}
                          </div>
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-muted-foreground" />
                          {client.country}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          client.is_active
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
                        }`}>
                          {client.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(client.created_at)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.client_id}`)}>
                              <User className="mr-2 h-4 w-4" />
                              View Client Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => navigate(`/clients/${client.client_id}/businesses`)}>
                              <Building2 className="mr-2 h-4 w-4" />
                              View Business Details
                            </DropdownMenuItem>
                            {canAddEdit('CLIENTS') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  onClick={() => handleDeleteClick(client)}
                                  className="text-destructive focus:text-destructive"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Mark Inactive
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {/* Pagination Controls - Inside card at bottom left */}
            <div className="flex items-center gap-3 px-4 py-2 border-t">
              <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                <SelectTrigger className="w-[70px] h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-muted-foreground">
                {startRecord}-{endRecord} {t('common.of')} {totalRecords}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={goToNextPage}
                  disabled={currentPage >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Mark Inactive Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark Client as Inactive</DialogTitle>
            <DialogDescription>
              Are you sure you want to mark <strong>{clientToDelete?.client_name}</strong> as inactive? This will not delete the client record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? t('common.loading') : 'Mark Inactive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
